import { Injectable } from "@angular/core";
import { take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";

enum MessageType {
  Success = "success",
  Error = "error",
  Info = "info",
  Warning = "warning"
}

interface MessageSettings {
  timeOut?: number;
  extendedTimeOut?: number;
  closeButton?: boolean;
  enableHtml?: boolean;
  tapToDismiss?: boolean;
}

@Injectable({
  providedIn: "root"
})
export class UserFeedbackService {

  constructor(
    private translateService: TranslateService,
    private toastr: ToastrService
  ) {
  }

  showSuccessMessage(
    translationKey: string,
    interpolationParams?: object,
    titleTranslationKey?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessage(translationKey, MessageType.Success, interpolationParams, titleTranslationKey, messageSettings);
  }

  showSuccessMessageWithoutTranslating(
    message: string,
    title?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessageWithoutTranslating(message, MessageType.Success, title, messageSettings);
  }

  showErrorMessage(
    translationKey: string,
    interpolationParams?: object,
    titleTranslationKey?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessage(translationKey, MessageType.Error, interpolationParams, titleTranslationKey, messageSettings);
  }

  showErrorMessageWithoutTranslating(
    message: string,
    title?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessageWithoutTranslating(message, MessageType.Error, title, messageSettings);
  }

  showInfoMessage(
    translationKey: string,
    interpolationParams?: object,
    titleTranslationKey?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessage(translationKey, MessageType.Info, interpolationParams, titleTranslationKey, messageSettings);
  }

  showInfoMessageWithoutTranslating(
    message: string,
    title?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessageWithoutTranslating(message, MessageType.Info, title, messageSettings);
  }

  showWarningMessage(
    translationKey: string,
    interpolationParams?: object,
    titleTranslationKey?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessage(translationKey, MessageType.Warning, interpolationParams, titleTranslationKey, messageSettings);
  }

  showWarningMessageWithoutTranslating(
    message: string,
    title?: string,
    messageSettings?: MessageSettings
  ): void {
    this.showMessageWithoutTranslating(message, MessageType.Warning, title, messageSettings);
  }


  private showMessage(
    translationKey: string,
    messageType: MessageType,
    interpolationParams?: object,
    titleTranslationKey?: string,
    messageSettings?: MessageSettings
  ): void {
    const translationObservables = [
      this.translateService.get(translationKey, interpolationParams)
    ];

    if (titleTranslationKey) {
      translationObservables.push(this.translateService.get(titleTranslationKey));
    }

    forkJoin(translationObservables)
      .pipe(take(1))
      .subscribe((responses: string[]) => {
        const message = responses[0];
        const messageTitle = responses.length > 1 ? responses[1] : undefined;

        this.toastr[messageType](message, messageTitle, messageSettings);
      });
  }

  private showMessageWithoutTranslating(
    message: string,
    messageType: MessageType,
    title?: string,
    messageSettings?: MessageSettings
  ): void {
    this.toastr[messageType](message, title, messageSettings);
  }
}
